import React from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router';

import { v4 as uuidv4 } from 'uuid';
import {
    AdvanceTable,
    HorizontalMenu,
    IBasicTableColumn,
    IBasicTableProps,
    IMenuItem,
    ITableToolbarProps,
    ITreeSelectDataNode,
    ITreeSelectDataSource,
    notification,
    showInformationDialog,
    Spinner,
    Typography,
} from 'ui-lib';

import { Route } from 'core';

import { useAuthStore, useDeviceGroupsStore, useStores } from '../../../../store';
import { AssignDevicesStore, IDeviceInfoProperties } from '../../../../store/assign-devices-store';
import { AccountType, IAccount } from '../../../../dto/access-management/account-dto';
import { DeviceGroupDto } from '../../../../dto/device-group-dto';
import { ModifyDeviceGroupComponent } from './device-group';
import { DEVICES_ROUTE_CODES } from '..';
import { NOTIFICATION_TYPE } from '../../../../dto/notifications-dto';
import { hasPermission } from '../../utils';
import { deleteDeviceGroupConfirm } from '../../admin/utils';
import { OrganisationSelect } from '../../components/organisation-select';

import manageDeviceGroupCss from './manage-device-group.css';
import { orderBy } from 'lodash';
import { isAllowedDeviceType, isNullOrEmpty } from 'common-utils';
import { showSavingErrorDialog } from 'pages/private/components/modal';
import { useUrlSearch } from 'hooks/use-search.hook';
import { useAccountTreeSelector } from 'hooks/accounts/account-datasource.hook';
import { useLoadingStates } from 'hooks/use-loading.hook';
import { TableUtils } from 'pages/private/components/table-utils';
import { useDeviceGroupPermissionContext } from 'contexts/permissions/device-group-permission.context';

export const enum MANAGE_DEVICE_GROUP_STATE {
    LIST,
    CREATE_OR_EDIT,
}

interface IDisplayDeviceGroup extends DeviceGroupDto {
    numberOfDevices: number;
}

interface IDeviceGroupCommonStates {
    onAccountChanged: (accountId: number) => void;
    treeSelectableDataSource: ITreeSelectDataSource[];
    deviceGroupLoading: boolean;
}

export const useDeviceGroupCommonStates = (
    options: {
        selectablePlatforms?: string[];
        renderNode?: (group: DeviceGroupDto) => Partial<{ disabled: boolean }>;
    } = {},
): IDeviceGroupCommonStates => {
    const [selectedAccountId, setSelectedAccountId] = React.useState<number>();
    const deviceGroupsStore = useDeviceGroupsStore();
    const { renderNode: renderDeviceGroupNode, selectablePlatforms = deviceGroupsStore.supportedPlatforms.map(p => p.code) } = options;

    const isSinglePlatform = selectablePlatforms?.length === 1;
    const _buildDeviceGroupTreeNode = (group: DeviceGroupDto): ITreeSelectDataSource => {
        if (!group) return undefined;
        const nodeProps = renderDeviceGroupNode ? renderDeviceGroupNode(group) : {};
        const value = {
            value: group.deviceGroupName,
            label: isSinglePlatform ? group.deviceGroupLabel : group.displayName,
            disabled: nodeProps.disabled || !selectablePlatforms.includes(group.deviceGroupPlatform),
        };
        if (isNullOrEmpty(group.childrenGroups)) return { treeDataNode: value };
        const childGroups = orderBy(group.childrenGroups, [(item) => item.deviceGroupLabel.trim().toLowerCase()]);
        const childNodes = childGroups.map(_buildDeviceGroupTreeNode);
        if (isNullOrEmpty(childNodes)) return { treeDataNode: value };

        return { treeDataNode: value, subTreeDataNode: childNodes };
    };

    React.useEffect(() => {
        deviceGroupsStore.loadDeviceGroupDetails();
    }, []);

    const deviceGroupsTree = React.useMemo<ITreeSelectDataSource[]>(() => {
        if (!selectedAccountId) return [];
        const devicegroups = deviceGroupsStore.getDevicesGroupDetails(selectedAccountId);
        return devicegroups.map(_buildDeviceGroupTreeNode);
    }, [selectedAccountId, deviceGroupsStore.allDeviceGroups, selectablePlatforms]);

    return {
        onAccountChanged: setSelectedAccountId,
        treeSelectableDataSource: deviceGroupsTree,
        deviceGroupLoading: deviceGroupsStore.dataLoading,
    };
};

interface IManageDeviceGroupContext {
    assignDevicesStore: AssignDevicesStore;
    clientAccountId: number;
    clientDeviceGroup: DeviceGroupDto;
    childGroup?: IDisplayDeviceGroup;
    isDeviceTypeSupported: (platform: string, type: string) => boolean;
    deleteGroup: (clientAccountId: number, group: DeviceGroupDto) => Promise<unknown>;
    navigation: {
        toDeviceGroupsTable: () => void;
    };
    onUpdating: (updating: boolean) => void;
}

const ManageDeviceGroupContext = React.createContext<IManageDeviceGroupContext>(null);

export const useManageDeviceGroupContext = () => React.useContext(ManageDeviceGroupContext);

export const ManageDeviceGroupsPage = observer(() => {
    const { currentUser } = useAuthStore();
    const store = useStores();
    const { apiClientStore, accountStore, deviceStore, notificationsStore, routeStore, deviceGroupsStore, commonStore } = store;

    const { loading, saving, withLoading, startSaving, finishSaving } = useLoadingStates();
    const { canManageDeviceGroup, canAssignDevices, runHandler } = useDeviceGroupPermissionContext();
    const navigate = useNavigate();
    
    const fileTransferRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.FILE_TRANSFER);
    const shellCommandRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.SHELL_COMMAND);
    const decommissioningRoute = routeStore.getRoute(DEVICES_ROUTE_CODES.DECOMMISSIONING);
    
    const [manageState, setManageState] = React.useState<MANAGE_DEVICE_GROUP_STATE>(MANAGE_DEVICE_GROUP_STATE.LIST);
    
    const [selectedDeviceGroup, setSelectedDeviceGroup] = React.useState<IDisplayDeviceGroup>();
    
    // If the logged in user is a client, then no need to use the accountUuid from the query string
    const { getSearchParam, updateSearchParams } = useUrlSearch();
    const selector = useAccountTreeSelector({
        selectableTypes: [AccountType.CLIENT],
        defaultSelectedAccount: currentUser.accountType === AccountType.CLIENT ? currentUser.accountUuid : getSearchParam('accountUuid'),
        onAccountChange: async (account: IAccount) => {
            if (currentUser.accountType === AccountType.CLIENT) {
                return;
            }
            updateSearchParams({ accountUuid: account?.uuid || null });
        },
    });

    const clientAccountId = React.useMemo(() => selector.selectedAccount?.id, [selector.selectedAccount]);

    const clientDeviceGroup = React.useMemo<DeviceGroupDto>(() => {
        return deviceGroupsStore.getClientDeviceGroupDetails(clientAccountId);
    }, [clientAccountId, deviceGroupsStore.allDeviceGroups]);

     const assignDevicesStore = React.useMemo(
        () =>
            new AssignDevicesStore({
                deviceGroupsStore,
                deviceStore,
                apiClientStore: apiClientStore,
            }),
        [],
    );

    React.useEffect(() => {
        deviceGroupsStore.loadDeviceGroupDetails(true);
    }, []);

    React.useEffect(() => {
        if (!clientAccountId || !clientDeviceGroup) return;
        (async () => {
            const resultEither = await withLoading(async () => {
                await assignDevicesStore.initialize(clientAccountId, clientDeviceGroup.deviceGroupName);
            });
            if (resultEither.isFail()) {
                showInformationDialog({
                    modalType: 'error',
                    content: resultEither.errorMessage,
                    title: 'Error',
                });
            }
        })();
    }, [clientDeviceGroup]);

    const managedDeviceGroupDetails = React.useMemo(() => {
        if (!clientDeviceGroup) return [];
        return clientDeviceGroup.childrenGroups?.map((child) => {
            const numberOfDevices = assignDevicesStore.displayDeviceDataSource.filter(
                (device) => device.deviceGroupName === child.deviceGroupName,
            ).length;
            return { ...child.toJson(), numberOfDevices } as IDisplayDeviceGroup;
        });
    }, [clientDeviceGroup, assignDevicesStore.displayDeviceDataSource]);

    const buildSubMenuItems = (record: Pick<IDisplayDeviceGroup, 'deviceGroupName'>): IMenuItem[] => {
        const _createSubMenuItem = (
            subMenuItems: IMenuItem[],
            route: Route,
            onClick: () => void,
            label?: string,
        ): void => {
            if (!hasPermission(route.code, store)) return;

            subMenuItems.push({
                code: route.code,
                label: () => label || route.label,
                iconName: route.iconName,
                onClick,
            });
        };

        const subMenuItem = [];
        commonStore.setSelectedOrganisation(
            currentUser.accountType === AccountType.CLIENT
                ? clientAccountId
                : accountStore.getAccountById(clientAccountId).parent.id,
        );

        _createSubMenuItem(subMenuItem, shellCommandRoute, () => {
            navigate(shellCommandRoute.path, {
                state: {
                    deviceGroupName: record.deviceGroupName,
                },
            });
        });
        _createSubMenuItem(subMenuItem, fileTransferRoute, () => {
            navigate(fileTransferRoute.path, {
                state: {
                    deviceGroupName: record.deviceGroupName,
                },
            });
        });
        _createSubMenuItem(subMenuItem, decommissioningRoute, () => {
            navigate(decommissioningRoute.path, {
                state: {
                    deviceGroupName: record.deviceGroupName,
                },
            });
        });

        return subMenuItem;
    };

    const ActionsMenu = (props: { record: IDisplayDeviceGroup }) => {
        return (
            <HorizontalMenu
                menuItems={[
                    {
                        code: 'actionsMenu',
                        label: () => 'Settings',
                        subMenuItems: buildSubMenuItems(props.record),
                        onClick: () => undefined,
                    },
                ]}
            />
        );
    };

    const deleteGroupByName = (accountId: number, deviceGroup: DeviceGroupDto) => {
        return runHandler('Device Groups', async () => {
            const result = await withLoading(async () => {
                const deviceGroupName = deviceGroup.deviceGroupName;

                // get devices for group
                assignDevicesStore.setCurrentDeviceGroup(deviceGroupName);

                if (!canAssignDevices && assignDevicesStore.getDevicesByGroupName(deviceGroupName).length > 0) {
                    return showSavingErrorDialog([
                        'Cannot delete device group including devices, because you do not have the permission to move devices.',
                    ]);
                }
                // Move devices to CLIENT account first
                const updatedResult = await assignDevicesStore.moveDevicesToClientGroup(deviceGroupName, true);
                if (!isNullOrEmpty(updatedResult.errors)) {
                    return showSavingErrorDialog(updatedResult.errors.map((item) => `${item.identifier}: `));
                }
                await deviceGroupsStore.deleteDeviceGroupByName(accountId, deviceGroupName);

                await notificationsStore.create({
                    content: {
                        title: 'Device groups',
                        text: `The device group ${deviceGroup.deviceGroupLabel} has been deleted successfully.`,
                        messageType: 'Device group',
                    },
                    type: NOTIFICATION_TYPE.PERSONAL,
                    receiverUserUuid: currentUser.uuid,
                    sender: currentUser.userName,
                });

                notification['info']({
                    message: `The device group ${deviceGroup.deviceGroupLabel} has been deleted successfully.`,
                    placement: 'topRight',
                });
            });
            if (result.isFail()) {
                showInformationDialog({
                    modalType: 'error',
                    content: result.errorMessage,
                    title: 'Delete error',
                });
            }
        });
    };

    const tableColumns: IBasicTableColumn[] = [
        {
            code: 'deviceGroupLabel',
            title: 'Name',
            dataIndex: 'deviceGroupLabel',
            width: '40%',
            defaultSortOrder: 'ascend',
            onFilter: (searchValue: string, group: IDisplayDeviceGroup) =>
                group.deviceGroupLabel?.toLowerCase().includes(searchValue.toLowerCase()),
            sorter: (a: IDisplayDeviceGroup, b: IDisplayDeviceGroup) =>
                a.deviceGroupLabel?.toLowerCase().localeCompare(b.deviceGroupLabel?.toLowerCase()),
            textSearchPlaceholder: 'Search device group',
            iconName: 'search',
            render: (text: string, record: IDisplayDeviceGroup) => (
                <Typography.Link
                    onClick={() => {
                        setSelectedDeviceGroup(record);
                        assignDevicesStore.setCurrentDeviceGroup(record.deviceGroupName);
                        setManageState(MANAGE_DEVICE_GROUP_STATE.CREATE_OR_EDIT);
                    }}
                >
                    {text}
                </Typography.Link>),
        },
        TableUtils.createBasicFilterColumn({ key: 'deviceGroupPlatform', title: 'Platform' }),
        {
            code: 'numberOfDevices',
            title: 'No. of devices',
            dataIndex: 'numberOfDevices',
            width: '15%',
            sorter: (a: IDisplayDeviceGroup, b: IDisplayDeviceGroup) => a.numberOfDevices - b.numberOfDevices,
        },
        {
            code: 'deviceGroupDescription',
            title: 'Description',
            dataIndex: 'deviceGroupDescription',
            width: '30%',
            sorter: (a: IDisplayDeviceGroup, b: IDisplayDeviceGroup) =>
                a.deviceGroupDescription?.toLowerCase().localeCompare(b.deviceGroupDescription?.toLowerCase()),
            onFilter: (searchValue: string, group: IDisplayDeviceGroup) =>
                group.deviceGroupDescription?.toLowerCase().includes(searchValue.toLowerCase()),
            textSearchPlaceholder: 'Search description',
            iconName: 'search',
        },
        {
            code: 'action',
            title: 'Action',
            dataIndex: 'action',
            width: '15%',
            render: (text: string, record: IDisplayDeviceGroup) => (
                <div className={manageDeviceGroupCss.tableActions}>
                    <ActionsMenu record={record} />
                    <Typography.Link
                        className={manageDeviceGroupCss.deleteButton}
                        hidden={!canManageDeviceGroup}
                        onClick={() => deleteDeviceGroupConfirm(deleteGroupByName, clientAccountId, record)}
                    >
                        Delete
                    </Typography.Link>
                </div>
            ),
        },
    ];

    const contextProps: IManageDeviceGroupContext = {
        assignDevicesStore: assignDevicesStore,
        clientAccountId: clientAccountId,
        clientDeviceGroup: clientDeviceGroup,
        childGroup: selectedDeviceGroup,
        deleteGroup: deleteGroupByName,
        isDeviceTypeSupported: (code: string, type: string) => {
            const platform = deviceGroupsStore.supportedPlatforms.find((p) => p.code == code);
            if (!platform) return false;
            return isAllowedDeviceType(platform.deviceTypes, type);
        },
        navigation: {
            toDeviceGroupsTable() {
                assignDevicesStore.setCurrentDeviceGroup(undefined);
                setSelectedDeviceGroup(undefined);
                setManageState(MANAGE_DEVICE_GROUP_STATE.LIST);
            },
        },
        onUpdating: (updating: boolean) => {
            if (updating) {
                startSaving();
            } else {
                finishSaving();
            }
        },
    };

    return (
        <Spinner className={manageDeviceGroupCss.spinning} spinning={saving || deviceGroupsStore.dataLoading}>
            <div>
                {manageState === MANAGE_DEVICE_GROUP_STATE.LIST && (
                    <div>
                        <div className={manageDeviceGroupCss.label}>
                            <Typography.Text>The inventory lists all the devices that you can manage.</Typography.Text>
                        </div>
                        {currentUser.accountType != AccountType.CLIENT && (
                            <OrganisationSelect
                                placeholder='Organisation'
                                treeDataSource={selector.dataSource}
                                filterTreeNode={(input, treeNode) => {
                                    return treeNode.label?.includes(input);
                                }}
                                expandAll={true}
                                onChange={selector.onChange}
                                value={selector.selectedAccount?.id}
                            />
                        )}
                        <AdvanceTable
                            loadingState={loading || deviceGroupsStore.dataLoading}
                            title={'Device groups'}
                            table={{
                                columns: tableColumns,
                                dataSource: managedDeviceGroupDetails,
                                rowKey: 'deviceGroupName',
                            }}
                            toolbar={{
                                onAdd: canManageDeviceGroup && !!selector.selectedAccount
                                    ? () => {
                                          assignDevicesStore.setCurrentDeviceGroup(uuidv4().replace(/-/g, ''));
                                          setManageState(MANAGE_DEVICE_GROUP_STATE.CREATE_OR_EDIT);
                                      }
                                    : undefined,
                            }}
                        />
                    </div>
                )}
                {manageState === MANAGE_DEVICE_GROUP_STATE.CREATE_OR_EDIT && (
                    <ManageDeviceGroupContext.Provider value={contextProps}>
                        <ModifyDeviceGroupComponent />
                    </ManageDeviceGroupContext.Provider>
                )}
            </div>
        </Spinner>
    );
});
